import {type FC, useRef} from 'react';
import {BackButton, CallButton, List, StyledNameDisplay, TableHeader} from '../types/VideoCallPage.styled.ts';
import type {CenterVideoCallInfo} from '../types';
import useVideoCallStore from '../../../store/useVideoCallStore.ts';
import callApi from '../../../apis/callApi.ts';
import useCenterStore from '../../../store/useCenterStore.ts';
import {FocusContext, setFocus, useFocusable} from '@noriginmedia/norigin-spatial-navigation';
import CenterList from './CenterList.tsx';

const VideoCallCenterList: FC = () => {
    const {centerInfo} = useCenterStore();
    const {
        handleBackToList,
        selectedCenterVideoCallInfoAry,
        setIsCalling,
        setRoomName,
        setTokenByRoomName,
        selectedBoroughs,
    } = useVideoCallStore();

    const createJitsiRoom = async (selectSeniorCenterList: Array<CenterVideoCallInfo>) => {
        const creator = centerInfo!.center_id;
        const unixTime = Math.floor(new Date().getTime() / 1000);
        const ListToText = creator + ',' + selectSeniorCenterList.map((item) => item.name).join(', ');
        const roomName = `ICSC-인천-영상통화-${ListToText}-${unixTime}`;

        const payload = {
            title: roomName,
            creator: creator,
            participants: selectSeniorCenterList.map((item) => String(item.centerid)),
        };

        const {data} = await callApi().post('/video/on/', payload);
        setRoomName(data.title);
        await setTokenByRoomName(centerInfo?.name ?? '');
    };

    const connectCall = async () => {
        if (selectedCenterVideoCallInfoAry.length === 0) {
            return;
        }
        await createJitsiRoom(selectedCenterVideoCallInfoAry);
        setIsCalling(true);
        setFocus('video-call-screen');
    };

    const containerRef = useRef<HTMLDivElement>(null);
    const {ref: backButtonRef, focused: backButtonFocused} = useFocusable({
        focusKey: 'back-to-list',
        onEnterPress: () => {
            handleBackToList();
            if (!selectedBoroughs) {
                setFocus('1-all');
            } else {
                setFocus(`${selectedBoroughs?.id}-${selectedBoroughs?.name}`);
            }
        },
        onArrowPress: (direction) => {
            if (direction === 'down') {
                setFocus('video-calling');
            }
            return false;
        },
        onFocus: () => {
            if (containerRef.current) {
                containerRef.current.scrollTo({top: 0, behavior: 'smooth'});
            }
        },
    });

    const {ref: videoCallingRef, focused: videoCallingFocused} = useFocusable({
        focusKey: 'video-calling',
        onEnterPress: connectCall,
        onArrowPress: (direction) => {
            if (direction === 'up') {
                setFocus('back-to-list');
            } else {
                if (!selectedBoroughs) return false;
                setFocus('center-list');
            }
            return false;
        },
        onFocus: () => {
            if (videoCallingRef.current) {
                videoCallingRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                });
            }
        },
    });

    const {ref: centerListRef, focusKey} = useFocusable({
        focusKey: 'center-list',
        trackChildren: true,
    });

    return (
        <div ref={containerRef} style={{width: '100%', height: '100%', padding: '10px', overflow: 'auto'}}>
            <BackButton ref={backButtonRef} onClick={handleBackToList} $focused={backButtonFocused}>
                &lt; 목록보기
            </BackButton>

            <TableHeader>
                <span>대상 경로당: &nbsp;</span>
                <StyledNameDisplay>
                    {selectedCenterVideoCallInfoAry.map((c: CenterVideoCallInfo) => c.name).join(',')}
                </StyledNameDisplay>
                <CallButton
                    $disabled={selectedCenterVideoCallInfoAry.length < 1}
                    onClick={() => connectCall()}
                    ref={videoCallingRef}
                    $focused={videoCallingFocused}
                >
                    전화걸기
                </CallButton>
            </TableHeader>

            <FocusContext.Provider value={focusKey}>
                <List ref={centerListRef}>
                    {selectedBoroughs &&
                        selectedBoroughs.centers.map((center: CenterVideoCallInfo) => (
                            <CenterList
                                key={center.id}
                                selectedBoroughsName={selectedBoroughs.name}
                                centerVideoCallInfo={center}
                            />
                        ))}
                </List>
            </FocusContext.Provider>
        </div>
    );
};

export default VideoCallCenterList;
